import DefaultCover from '@assets/media/no_info_cover.png';
import {
  FocusContext,
  SpatialNavigation,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import {
  getAllEpgsForSeries,
  loadOlderEpgsForChannel,
  playVideo,
  playVideoVod,
  setPlayerControlsState,
  Translation
} from 'common/actions';
import { Button } from 'common/components/button';
import { Card } from 'common/components/cards';
import { DescriptionStaff } from 'common/components/description';
import { Image } from 'common/components/image';
import { Stripe, StripeView } from 'common/components/stripe';
import VerticalSeparator from 'common/components/VerticalSeparator';

import Chevron from '@assets/media/chevron.svg';
import Icon from 'common/components/Icon';
import StripeViewSkeleton from 'common/components/stripe-view-skeleton/StripeViewSkeleton';
import { THUMBNAIL_OFFSET } from 'common/config/constants';
import variables from 'common/config/variables';
import {
  CategoriesOptions,
  Epg,
  IViewWithDetailsStripeItem,
  LiveChannels
} from 'common/constants/data-types';
import { GroupItemData } from 'common/interfaces';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import {
  getChannelLogo,
  getDayOfWeekAndDate,
  getStartOfDayDateObject,
  getStartOfDayTimestampFast,
  getThumbnailUrl,
  getToday,
  getVodThumbnailUrl
} from 'common/services/helpers';
import { getClassName, getScalablePixel } from 'common/utils';
import { useDebounce, usePlayerActions } from 'common/utils/hooks';
import _, { clamp, debounce, DebouncedFunc, isEmpty, uniqueId } from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react';
import { connect } from 'react-redux';
import RatingPie from './RatingPie';
import { reduceEpisodes } from './utils';
import VodSeasonStripeFooter from './VodSeasonStripeFooter';

type TView = 'programme' | 'episode' | 'info' | string;

interface ProgrammeToggleProps {
  onToggle?: (active: boolean) => void;
  i18n: Translation;
  logo: string;
  active?: boolean;
  channelName: string;
}

// eslint-disable-next-line react/display-name
const ProgrammeToggle = React.memo((props: ProgrammeToggleProps) => {
  const { ref, focused } = useFocusable({
    onEnterPress: () => props.onToggle && props.onToggle(!props.active),
    onArrowPress: (direction: string) => {
      if (direction === 'right') props.onToggle && props.onToggle(true);
      else if (direction === 'left') props.onToggle && props.onToggle(false);
      return true;
    }
  });

  if (isEmpty(props.i18n)) return null;

  return (
    <div
      ref={ref}
      className={getClassName('programme-toggle-container', { focused, active: props.active })}
      data-test-id="programme-toggle-container"
      data-future={props.active}
    >
      <div className="toggle-state">
        <Icon src={Chevron} size="verySmall" />
        <div className="title-container">
          <span className="title">{props.i18n.extendedBottomView.archive}</span>
          <span className="subtitle">{props.i18n.extendedBottomView.archiveSubtitle}</span>
        </div>
      </div>
      <div className="channel-logo-container">
        <Image className="channel-logo" src={props.logo} />
        <span className="channel-name">{props.channelName}</span>
      </div>
      <div className="toggle-state active">
        <div className="title-container">
          <span className="title">{props.i18n.extendedBottomView.upcoming}</span>
          <span className="subtitle">{props.i18n.extendedBottomView.upcomingSubtitle}</span>
        </div>
        <Icon src={Chevron} size="verySmall" />
      </div>
      <span className="selector" />
    </div>
  );
});

interface ButtonProps {
  title: string;
  active: boolean;
  type: string | number;
  focusKey: string;
  onFocus: VoidFunction;
  onSelect: VoidFunction;
  testId: string;
}
const ButtonSelector = (props: ButtonProps) => {
  const debounceRef = useRef<DebouncedFunc<VoidFunction>>(undefined);

  const onSelect = useCallback(() => {
    props.onSelect();
  }, [props.onSelect]);

  const onFocus = useCallback(() => {
    props.onFocus();
    debounceRef.current?.cancel();
    debounceRef.current = debounce(() => {
      onSelect();
    }, variables.SCROLL_ANIMATION_DURATION);
    debounceRef.current();
  }, [props.onFocus, onSelect]);

  return (
    <Button
      title={props.title}
      className={getClassName('extended-bottom-view-button', {
        active: props.type === props.type && props.active
      })}
      key={`extended-bottom-view-button-${props.type}`}
      focusKey={`${props.focusKey}-${props.type}`}
      size="large"
      onFocus={onFocus}
      onClick={props.onSelect}
      testId={`extended-bottom-view-button-${props.type}`}
    />
  );
};

interface ButtonStripeProps {
  focusKey: string;
  activeType?: string | number;
  active?: boolean;
  list: { type: string | number; title: string }[];
  visible?: boolean;
  onActive?: (type: string | number) => void;
}
// eslint-disable-next-line react/display-name
const ButtonStrip = React.memo((props: ButtonStripeProps) => {
  const [isFocusBoundary, setIsFocusBoundary] = useState(false);
  const { ref, focusKey } = useFocusable({
    focusKey: props.focusKey,
    saveLastFocusedChild: false,
    isFocusBoundary,
    focusBoundaryDirections: ['down'],
    trackChildren: false,
    preferredChildFocusKey: props.activeType ? `${props.focusKey}-${props.activeType}` : undefined
  });

  const list = useMemo(
    () =>
      props.list.map((listItem) => {
        return (
          <ButtonSelector
            key={`extended-bottom-view-button-${listItem.type}`}
            title={listItem.title}
            type={listItem.type}
            active={!!(props.activeType === listItem.type && props.active)}
            focusKey={focusKey}
            onFocus={() => setIsFocusBoundary(true)}
            onSelect={() => {
              props.onActive && props.onActive(listItem.type);
              setIsFocusBoundary(false);
            }}
            testId={`extended-bottom-view-button-${listItem.type}`}
          />
        );
      }),
    [props.list, props.activeType, props.active, props.onActive, focusKey]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={getClassName('buttons-strip', { visible: props.visible })} ref={ref}>
        {list}
      </div>
    </FocusContext.Provider>
  );
});

interface ExtendedBottomViewProps {
  // Redux
  liveChannels: LiveChannels;
  logos: any;
  i18n: Translation;
  epgs: IVideoPlayer['allEpgs'];
  currentEpg: Epg;
  playVideoVod: (currentepg: Epg, options?: any) => void;
  getAllEpgsForSeries: (baseContentId: string) => Promise<Epg[] | undefined>;
  loadOlderEpgsForChannel: (channelId: string, untilTimestamp?: number) => void;
  isPlayerPlaying?: boolean;
  vod?: boolean;
  vodAsset?: Epg;
  categoriesOptions?: CategoriesOptions;
  isLiveEpg?: number;
  //
  visible?: boolean;
  closed?: boolean;
  onFocus?: VoidFunction;
  onBlur?: VoidFunction;
  onPlay?: VoidFunction;
}

function ExtendedBottomView(props: ExtendedBottomViewProps) {
  // Generate unique hash key every time the view is "closed"
  // So that the focus resets itself
  const [hashKey, setHashKey] = useState(uniqueId('extended-bottom-view'));
  const [episodeRows, setEpisodeRows] = useState<GroupItemData[]>([]);
  const [programmeFuture, setProgrammeFuture] = useState(false);
  const debouncedProgrammeFuture = useDebounce(programmeFuture, variables.LONG_THROTTLE_TIMEOUT);
  const { ref, hasFocusedChild, focusKey } = useFocusable({
    preferredChildFocusKey: 'extended-bottom-view-button-strip',
    trackChildren: true,
    focusBoundaryDirections: ['left', 'right'],
    saveLastFocusedChild: false,
    isFocusBoundary: true,
    onFocus: props.onFocus,
    onBlur: props.onBlur
  });
  const [focusedEpisode, setFocusedEpisode] = useState<Epg | undefined>();
  const [focusedEpisodePending, startTransition] = useTransition();
  const [focusedSeasonRow, setFocusedSeasonRow] = useState<number | undefined>(undefined);
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();

  // Used for optimizing the initial view when closed
  // So that it doesn't reset to the first view when the user hasn't opened it
  const hasBeenOpenedByUser = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => {
      SpatialNavigation.updateLayout(focusKey);
    }, variables.LONG_THROTTLE_TIMEOUT);
    return () => {
      clearTimeout(id);
    };
  }, [hashKey, focusKey]);

  const isSeries = useMemo(
    () =>
      !isEmpty(props.currentEpg) &&
      // VOD Series
      (props.vod
        ? props.epgs?.length && props.epgs?.length > 1
        : // Normal EPG
          props.currentEpg?.baseContentId != '0' && !isEmpty(props.currentEpg?.baseContentId)),
    [props.currentEpg?.baseContentId, props.epgs?.length, props.vod]
  );

  const buttonStripItems: { type: TView; title: string }[] = useMemo(
    () => [
      ...(!props.vod
        ? [
            {
              type: 'programme',
              title: props.i18n.common.programme
            }
          ]
        : []),
      ...(isSeries
        ? [
            {
              type: 'episode',
              title: props.i18n.common.episodes
            }
          ]
        : []),
      {
        type: 'info',
        title: props.i18n.common.information
      }
    ],
    [props.i18n, isSeries, props.vod]
  );

  const shouldParseEpgs = useMemo(() => {
    return props.vod ? true : props.isPlayerPlaying;
  }, [props.vod, props.isPlayerPlaying]);

  const initialView = useMemo(() => buttonStripItems[0].type, [buttonStripItems]);
  const [view, setView] = useState<TView>(initialView);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (props.closed) {
      timeout = setTimeout(() => {
        setView((current) => (current !== initialView ? initialView : current));
        if (hasBeenOpenedByUser.current) {
          setHashKey(uniqueId('extended-bottom-view'));
          hasBeenOpenedByUser.current = false;
        }
        setProgrammeFuture(false);
      }, variables.LONG_THROTTLE_TIMEOUT);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [props.closed, initialView]);

  const getProgrammeDay = useCallback(
    (timestamp: number | string) => {
      if (isEmpty(props.i18n)) return 'NaN';

      try {
        const date = getStartOfDayDateObject(Number(timestamp));
        return getDayOfWeekAndDate(date, props.i18n.days, props.i18n.months, {
          today: props.i18n.common.today,
          yesterday: props.i18n.common.yesterday
        }).toUpperCase();
      } catch (error) {
        console.warn(error);
      }
      return timestamp.toString().toUpperCase();
    },
    [props.i18n]
  );

  const programmeRows = useMemo(() => {
    const { epgs } = props;

    // Return empty if no epgs or no i18n
    // or if the epg list has only the current epg
    if (
      isEmpty(epgs) ||
      isEmpty(props.i18n.common) ||
      epgs?.every((e) => e?.id === props.currentEpg?.id)
    ) {
      return [];
    }
    const now = getToday().getTime() / 1000;
    if (epgs) {
      const formated = _(
        epgs
          .filter((e) => (debouncedProgrammeFuture ? e.start > now : e.start < now))
          .sort((a, b) => (debouncedProgrammeFuture ? a.start - b.start : b.start - a.start))
      )
        .groupBy((e) => getStartOfDayTimestampFast(e.start)) // Group by day using epoch days
        .map((value, key) => ({
          title: getProgrammeDay(parseInt(key) * 86400 * 1000),
          items: value
        }))
        .value();
      return debouncedProgrammeFuture ? formated : formated.reverse();
    }
    return [];
  }, [props.epgs, props.i18n.common, debouncedProgrammeFuture]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const idle = requestIdleCallback(() => {
      if (shouldParseEpgs) {
        timeout = setTimeout(async () => {
          try {
            const { currentEpg, i18n } = props;

            if (isEmpty(currentEpg) || isEmpty(i18n) || !isSeries) return;

            const epgs = props.vod
              ? props.epgs
              : ((await props.getAllEpgsForSeries(currentEpg.baseContentId)) as Epg[]);

            if (epgs) {
              const now = getToday().getTime() / 1000;

              const unknownSeries: (Epg & { future: boolean })[] = [];
              const knownSeries = epgs
                // Remove the unknown series from the main list
                // So it can be later added to another list
                .filter((e) => {
                  if (e.numeration.SeasonNumber == 0) {
                    unknownSeries.push({ ...e, future: e.start > now });
                    return false;
                  }
                  return true;
                });

              const reducedEpgs = props.vod
                ? knownSeries
                : reduceEpisodes(knownSeries, props.liveChannels);

              const formated = _(
                reducedEpgs
                  // Sort EPGs by episode number
                  .sort((a, b) => a.numeration?.EpisodeNumber - b.numeration?.EpisodeNumber)
              )
                .groupBy((e) => e.numeration.SeasonNumber)
                .map((value, key) => {
                  return {
                    title: `${i18n.common.season.toUpperCase()} ${key}`,
                    // Inject if the current epg is in the future
                    items: value.map((e) => ({ ...e, future: e.start > now }))
                  };
                })
                .value();

              // Add the unknown series as another season at the end
              !isEmpty(unknownSeries) &&
                formated.push({
                  title: props.i18n.common.other.toUpperCase(),
                  // Sort the unknown series by the start of the epg
                  items: unknownSeries.sort((a, b) => a.start - b.start)
                });
              //

              setEpisodeRows(formated);
            } else {
              setEpisodeRows([]);
            }
          } catch (error) {
            console.warn(error);
          }
        }, variables.EPGS_LOAD_THROTTLE_TIMEOUT);
      }
    });

    return () => {
      cancelIdleCallback(idle);
      clearTimeout(timeout);
      setEpisodeRows([]);
    };
  }, [props.currentEpg?.baseContentId, props.i18n, isSeries, shouldParseEpgs]);

  const coverImage = useMemo(() => {
    try {
      const currentEpg = props.vod ? props.vodAsset : props.currentEpg;
      if (isEmpty(currentEpg)) return DefaultCover;
      const cover = currentEpg?.photos?.find((e) => e.type === 'cover');
      const wallpaper = currentEpg?.photos?.find((e) => e.type === 'wallpaper');
      return (
        cover?.url ||
        wallpaper?.url ||
        getThumbnailUrl(currentEpg.chan_id, props.isLiveEpg, currentEpg.start + THUMBNAIL_OFFSET)
      );
    } catch (error) {
      console.warn(error);
      return DefaultCover;
    }
  }, [props.currentEpg, props.vod, props.vodAsset, props.isLiveEpg]);

  const openPlayer = useCallback(
    (epg: IViewWithDetailsStripeItem) => {
      const now = getToday().getTime() / 1000;
      if (epg.start > now) return;

      if (props.vod) {
        props.playVideoVod(epg, {
          isFullscreen: true
        });
      } else {
        playVideo(epg, { isFullscreen: true, continueWatching: true });
      }
      props.onPlay && props.onPlay();
    },
    [props.onPlay, props.vod]
  );

  const onStripeFocus = useCallback(() => {
    hasBeenOpenedByUser.current = true;
  }, []);

  const onProgrammeRowFocus = useCallback(
    (index: number, count: number) => {
      const channelId = props.currentEpg?.chan_id;
      if (!debouncedProgrammeFuture && channelId && index >= count - 2) {
        props.loadOlderEpgsForChannel(channelId);
      }
    },
    [debouncedProgrammeFuture, props.currentEpg?.chan_id]
  );

  const onStripeBlur = useCallback(() => {
    setFocusedSeasonRow(undefined);
    setFocusedEpisode(undefined);
  }, []);

  const onEpisodeFocus = useCallback(
    (row: number) => (epg: Epg) => {
      if (!props.vod) return;
      startTransition(() => {
        setFocusedSeasonRow(row);
        setFocusedEpisode(epg);
      });
    },
    [props.vod]
  );

  const renderArchiveCard = useCallback(
    (item: Epg, index: number) => {
      return (
        <Card
          key={`player-bottom-view-card-${item.eventid}`}
          epg={item}
          isLive={!!item.now}
          showDate={false}
          logo={getChannelLogo(item.chan_id, props.logos)}
          testId={`player-bottom-view-card-${index}`}
          i18n={props.i18n}
        />
      );
    },
    [props.logos, props.i18n]
  );

  const getEpisodeTitle = useCallback(
    (item: Epg & { future?: boolean }) => {
      if (item.numeration.EpisodeNumber) {
        return `${props.i18n?.common?.episode} ${item.numeration.EpisodeNumber}`.toUpperCase();
      } else if (item.now) {
        return props.i18n?.common?.episodeAiring;
      } else {
        return item.future
          ? props.i18n?.common?.episodeWillAirAt
          : props.i18n?.common?.episodeAiredAt;
      }
    },
    [props.i18n.common]
  );

  const renderEpisodeCard = useCallback(
    (item: Epg & { future?: boolean }, index: number) => {
      let title = '';
      if (props.categoriesOptions?.[item.majorId]?.USE_ASSET_TITLE_FOR_EPISODE) {
        title = item.title;
      } else {
        title = getEpisodeTitle(item);
      }
      let thumbnailUrl = undefined;
      if (props.vod) {
        const thumbnail = item.photos.find((photo) => photo.type === 'thumbnail')?.url;
        thumbnailUrl =
          props.categoriesOptions?.[item.majorId]?.USE_ASSET_THUMBMAIL_FOR_EPISODE_CARD && thumbnail
            ? thumbnail
            : getVodThumbnailUrl(item.id, 4 * 60);
      }
      return (
        <Card
          key={`player-bottom-view-episode-card-${item.eventid}`}
          epg={item}
          logo={!props.vod && getChannelLogo(item.chan_id, props.logos)}
          // Always get the thumbnail from the vod at the 4 minute mark
          thumbnailUrl={thumbnailUrl}
          title={title}
          showDate={!props.vod}
          showTime={!props.vod}
          showRating={!props.vod}
          testId={`player-bottom-view-episode-card-${!!item.future}-${index}`}
          i18n={props.i18n}
        />
      );
    },
    [props.logos, props.vod, props.categoriesOptions, props.i18n]
  );

  const renderProgrammeList = useMemo(() => {
    const chanId = props.currentEpg?.chan_id;
    return (
      <StripeView
        key="player-controls-programme-view"
        focusKey="player-controls-programme-view"
        loading={!programmeRows.length}
        // the shimmer animates every frame, even while the view is off-screen
        loader={!programmeRows.length && !props.closed && <StripeViewSkeleton />}
        saveFocusOnUnmount={false}
        onFocus={onStripeFocus}
        onRowFocus={onProgrammeRowFocus}
        stripeHeader={
          <ProgrammeToggle
            active={programmeFuture}
            onToggle={setProgrammeFuture}
            i18n={props.i18n}
            channelName={props.liveChannels[chanId]?.name}
            logo={getChannelLogo(props.currentEpg?.chan_id, props.logos)}
          />
        }
      >
        {programmeRows.map((row, index) => {
          const key = `player-bottom-view-programme-stripe-${index}-${debouncedProgrammeFuture}`;
          return (
            <Stripe
              key={key}
              title={row.title}
              onStripeItemPress={openPlayer}
              items={row.items}
              renderItem={renderArchiveCard}
              testId={'player-bottom-view-programme-stripe-' + index}
            />
          );
        })}
      </StripeView>
    );
  }, [
    programmeRows,
    props.currentEpg,
    props.logos,
    props.closed,
    programmeFuture,
    debouncedProgrammeFuture
  ]);

  const renderEpisodeList = useMemo(() => {
    return (
      <StripeView
        key="player-controls-episode-list"
        focusKey="player-controls-episode-list"
        saveFocusOnUnmount={false}
        scrollPadding={-variables.STRIPE_ITEM_PADDING}
        onBlur={onStripeBlur}
        onFocus={onStripeFocus}
        loading={Boolean(!episodeRows.length && isSeries)}
        disableVirtual={props.vod}
      >
        {episodeRows.map((row, index) => {
          return (
            <Stripe
              key={`player-bottom-view-seasons-stripe-${index}`}
              title={row.title}
              onStripeItemPress={openPlayer}
              onStripeItemFocus={onEpisodeFocus(index)}
              items={row.items}
              renderItem={renderEpisodeCard}
              testId={'player-bottom-view-seasons-stripe-' + index}
              stripeFooter={
                props.vod && (
                  <VodSeasonStripeFooter
                    currentEpg={focusedEpisode}
                    pending={focusedEpisodePending}
                    hidden={!focusedEpisode}
                    visible={focusedSeasonRow === index && !!focusedEpisode}
                  />
                )
              }
            />
          );
        })}
      </StripeView>
    );
  }, [episodeRows, focusedSeasonRow, focusedEpisode, focusedEpisodePending, props.vod, isSeries]);

  const renderInfoPage = useMemo(() => {
    const currentEpg = props.vod ? props.vodAsset : props.currentEpg;
    if (isEmpty(currentEpg)) return null;
    const hasStaff = !isEmpty(currentEpg.persons) || !isEmpty(currentEpg.actors);

    const renderRatings = () => {
      if (isEmpty(currentEpg.rating)) return null;

      const entries = Object.entries(currentEpg.rating).filter(([key]) =>
        ['Action', 'Depth', 'Humor', 'Suspense'].includes(key)
      );

      // Skip rendering when every rate is 0
      if (entries.every(([key, value]) => !value)) return null;

      return entries.map(([key, value], index) => (
        <React.Fragment key={`${key}-${value}`}>
          <RatingPie
            rating={clamp(value, 0, 5)}
            title={props.i18n.rating.titles[key.toLowerCase()]}
          />
          {index < entries.length - 1 && <VerticalSeparator height={getScalablePixel(88)} />}
        </React.Fragment>
      ));
    };
    return (
      <div className="info-page">
        <div className="cover-image-holder">
          <Image src={coverImage} fallbackSrc={DefaultCover} className="cover-image" />
          {!isEmpty(currentEpg.rating) && <div className="rating-holder">{renderRatings()}</div>}
        </div>
        <div className="description-container">
          <span className="description">
            {currentEpg.fulltext || currentEpg.shorttext || currentEpg.subtitle}
          </span>
          {hasStaff && (
            <div className="staff">
              <DescriptionStaff
                align="left"
                persons={currentEpg.persons}
                actors={currentEpg.actors}
              />
            </div>
          )}
        </div>
      </div>
    );
  }, [props.currentEpg, coverImage, props.i18n.rating, props.vod, props.vodAsset]);

  const renderView = () => {
    switch (view) {
      case 'episode':
        return renderEpisodeList;
      case 'programme':
        return renderProgrammeList;
      default:
      case 'info':
        return renderInfoPage;
    }
  };

  return (
    <Fragment key={hashKey}>
      <FocusContext.Provider value={focusKey}>
        <div
          className={getClassName('extended-bottom-view-container', { focused: hasFocusedChild })}
          ref={ref}
        >
          <ButtonStrip
            focusKey="extended-bottom-view-button-strip"
            list={buttonStripItems}
            onActive={setView as any}
            activeType={view}
            visible={props.visible}
            active={hasFocusedChild}
          />
          {renderView()}
        </div>
      </FocusContext.Provider>
    </Fragment>
  );
}
const mapStateToProps = ({ liveChannels, logos, i18n, videoPlayer, epgs }: any) => ({
  liveChannels,
  logos,
  i18n,
  epgs: videoPlayer.epgs?.all,
  isLiveEpg: videoPlayer.video?.isLiveEpg,
  currentEpg: videoPlayer?.epgs?.current,
  isPlayerPlaying: videoPlayer.isPlaying,
  vod: videoPlayer.video?.vod,
  vodAsset: videoPlayer.video?.vodAsset,
  categoriesOptions: epgs[videoPlayer.video?.vod ? 'vod' : 'recorded']?.categoriesOptions
});

export default React.memo(
  connect(mapStateToProps, {
    setPlayerControlsState,
    playVideo,
    playVideoVod,
    getAllEpgsForSeries,
    loadOlderEpgsForChannel
  })(ExtendedBottomView)
);
